import React, { memo, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { DashboardRoutes } from "../../routes/dashboard";
import { TWclassNames } from "../utils/Div";
import RoleOnly from "../../routes/roleOnly";
import { useCanManageHoursMonthlyOverview } from "../../routes/roleOnly";
import { useCanApproveHours, useCanConfirmAgreedOvertime, useCanViewHoursCompanyOverview } from "../../routes/roleOnly";
import { useSettings } from "../../contexts/settings-context";
import "./home/dashboard-design.css";
import "./dashboard-chrome.css";
import "./sidebar-nav.css";

const matchRoutePath = (pathname, routePath) => {
  if (!routePath) return false;

  const pathParts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  const routeParts = routePath.replace(/\/+$/, "").split("/").filter(Boolean);

  if (pathParts.length < routeParts.length) return false;

  return routeParts.every((segment, index) => {
    if (segment.startsWith(":")) return true;
    return segment === pathParts[index];
  });
};

const routeSpecificity = (routePath) =>
  routePath.split("/").filter((segment) => segment && !segment.startsWith(":")).length;

const findBestMenuState = (
  items,
  pathname,
  parentKey = "",
  topLevelTitle = null,
  level = 0,
  expandKeyAtLevel1 = null
) => {
  let best = null;

  items.forEach((item, idx) => {
    const key = parentKey ? `${parentKey}-${item.title}` : `route-${idx}-${item.title}`;
    const topTitle = topLevelTitle ?? item.title;
    const sidebarChildren = (item.children || []).filter((child) => child.sidebar);
    const subHasChildren = sidebarChildren.some((child) =>
      (child.children || []).some((grandchild) => grandchild.sidebar)
    );
    const currentExpandKey = level === 1 && subHasChildren ? key : expandKeyAtLevel1;

    if (sidebarChildren.length > 0) {
      const nested = findBestMenuState(
        sidebarChildren,
        pathname,
        key,
        topTitle,
        level + 1,
        currentExpandKey
      );
      if (nested && (!best || nested.specificity > best.specificity)) {
        best = nested;
      }
    }

    if (item.path && matchRoutePath(pathname, item.path)) {
      const specificity = routeSpecificity(item.path);
      if (!best || specificity > best.specificity) {
        best = {
          openMenu: topTitle,
          openSubMenu: currentExpandKey,
          specificity,
        };
      }
    }
  });

  return best;
};

const SidebarNav = memo(({ onClick, collapsed }) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const menuItemRefs = useRef({});
  const subMenuItemRefs = useRef({});
  const canManageHoursMonthlyOverview = useCanManageHoursMonthlyOverview();
  const canViewHoursCompanyOverview = useCanViewHoursCompanyOverview();
  const canApproveHours = useCanApproveHours();
  const canConfirmAgreedOvertime = useCanConfirmAgreedOvertime();

  const scrollMenuItemIntoView = useCallback(
    (element) => {
      if (!element || collapsed) return;

      requestAnimationFrame(() => {
        element.scrollIntoView({ block: "start", behavior: "smooth" });
      });
    },
    [collapsed]
  );

  const routes = useMemo(
    () => DashboardRoutes.filter((item) => item.sidebar),
    []
  );

  const bottomRoutes = useMemo(
    () => DashboardRoutes.filter((item) => item.sidebarBottom),
    []
  );

  useEffect(() => {
    const menuState = findBestMenuState(routes, location.pathname);
    setOpenMenu(menuState?.openMenu ?? null);
    setOpenSubMenu(menuState?.openSubMenu ?? null);
  }, [location.pathname, routes]);

  useEffect(() => {
    if (!openMenu) return;
    scrollMenuItemIntoView(menuItemRefs.current[openMenu]);
  }, [openMenu, openSubMenu, scrollMenuItemIntoView]);

  useEffect(() => {
    if (!openSubMenu) return;
    scrollMenuItemIntoView(subMenuItemRefs.current[openSubMenu]);
  }, [openSubMenu, scrollMenuItemIntoView]);

  const isActiveRoute = (path) => location.pathname === path;

  const menuLabel = (title) => (title ? t(title, { defaultValue: title }) : "");

  const handleToggle = (title, hasChildren) => {
    if (hasChildren) {
      setOpenMenu((prev) => {
        if (prev === title) {
          setOpenSubMenu(null);
          return null;
        }
        setOpenSubMenu(null);
        return title;
      });
    } else {
      onClick(); // collapse mobile menu
    }
  };

  const handleSubMenuToggle = (title) => {
    setOpenSubMenu((prev) => (prev === title ? null : title));
  };

  // Recursive component for rendering nested menu items
  const renderMenuItem = (item, level = 0, parentKey = "") => {
    const key = parentKey ? `${parentKey}-${item.title}` : item.title;
    const current = isActiveRoute(item.path);
    const hasChildren = item?.children?.some((child) => child.sidebar);
    const sidebarChildren = item?.children?.filter((child) => {
      if (!child.sidebar) return false;
      if (child.path === "/dashboard/hours/monthly-overview") {
        return canManageHoursMonthlyOverview;
      }
      if (child.path === "/dashboard/hours/time-overview") {
        return canViewHoursCompanyOverview;
      }
      if (child.path === "/dashboard/hours/approval") {
        return canApproveHours || canConfirmAgreedOvertime;
      }
      return true;
    }) || [];
    const isOpen = level === 0 ? openMenu === item.title : openSubMenu === key;

    if (level === 0) {
      // Top level menu items
      return (
        <RoleOnly
          key={key}
          role={item?.role}
          roles={item?.roles}
          feature={item?.feature}
          action={item?.action}
          requireAdmin={item?.requireAdmin}
          requireAllRoles={item?.requireAllRoles}
        >
          <li
            className="min-w-0"
            ref={(element) => {
              menuItemRefs.current[item.title] = element;
            }}
          >
            {!hasChildren ? (
              <Link
                to={item.sidebarTo || item.path}
                onClick={() => handleToggle(item.title, hasChildren)}
                className={TWclassNames(
                  current ? "pd-sidebar-nav__item--active" : "",
                  "pd-sidebar-nav__item group flex items-center gap-2 rounded-xl p-2 text-sm font-semibold cursor-pointer transition-all duration-300"
                )}
                title={collapsed ? menuLabel(item.title) : undefined}
                aria-current={current ? "page" : undefined}
              >
                <item.icon className="h-6 w-6 shrink-0" />
                {!collapsed && (
                  <span className="min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 w-auto opacity-100">
                    {menuLabel(item.title)}
                  </span>
                )}
                {item.count && !collapsed && (
                  <span className="pd-sidebar-nav__count ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-900 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-gray-400 ring-1 ring-inset ring-gray-700">
                    {item.count}
                  </span>
                )}
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleToggle(item.title, hasChildren)}
                  aria-expanded={isOpen}
                  title={collapsed ? menuLabel(item.title) : undefined}
                  className={TWclassNames(
                    current || isOpen ? "pd-sidebar-nav__item--active" : "",
                    "pd-sidebar-nav__item group flex w-full items-center gap-2 rounded-xl p-2 text-left text-sm font-semibold cursor-pointer transition-all duration-300"
                  )}
                >
                  <item.icon className="h-6 w-6 shrink-0" />
                  {!collapsed && (
                    <span className="min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 w-auto opacity-100">
                      {menuLabel(item.title)}
                    </span>
                  )}
                  {item.count && !collapsed && (
                    <span className="pd-sidebar-nav__count ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-900 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-gray-400 ring-1 ring-inset ring-gray-700">
                      {item.count}
                    </span>
                  )}
                </button>

                {hasChildren && !collapsed && isOpen && (
                  <ul className="pt-2 pl-8 flex flex-col gap-1">
                    {sidebarChildren.map((subItem) => (
                      <RoleOnly
                        key={`sub-${key}-${subItem.title}`}
                        role={subItem?.role}
                        roles={subItem?.roles}
                        feature={subItem?.feature}
                        action={subItem?.action}
                        requireAdmin={subItem?.requireAdmin}
                        requireAllRoles={subItem?.requireAllRoles}
                        drivingFleetVehiclesList={
                          subItem?.drivingFleetVehiclesList
                        }
                      >
                        <li className="min-w-0">
                          {renderMenuItem(subItem, level + 1, key)}
                        </li>
                      </RoleOnly>
                    ))}
                  </ul>
                )}
              </>
            )}
          </li>
        </RoleOnly>
      );
    } else {
      // Nested menu items (level 1+)
      const subHasChildren = item?.children?.some((child) => child.sidebar);
      const subSidebarChildren = item?.children?.filter((child) => child.sidebar) || [];
      const subIsOpen = openSubMenu === key;
      const subCurrent = isActiveRoute(item.path);

      return (
        <>
          {!subHasChildren ? (
            <Link
              to={item.sidebarTo || item.path}
              onClick={() => onClick()}
              className={TWclassNames(
                subCurrent ? "pd-sidebar-nav__item--active" : "",
                "pd-sidebar-nav__item group flex items-center whitespace-nowrap rounded-md py-2 pl-2 pr-2 text-sm transition-colors"
              )}
              aria-current={subCurrent ? "page" : undefined}
            >
              <ArrowRightIcon className="h-4 w-4 mr-2" />
              {menuLabel(item.title)}
            </Link>
          ) : (
            <>
              <button
                type="button"
                ref={(element) => {
                  subMenuItemRefs.current[key] = element;
                }}
                onClick={() => handleSubMenuToggle(key)}
                aria-expanded={subIsOpen}
                className={TWclassNames(
                  subCurrent || subIsOpen ? "pd-sidebar-nav__item--active" : "",
                  "pd-sidebar-nav__item group flex w-full items-center justify-between rounded-md py-2 pl-2 pr-2 text-left text-sm cursor-pointer transition-colors"
                )}
              >
                <div className="flex items-center whitespace-nowrap">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  {menuLabel(item.title)}
                </div>
                <ChevronRightIcon
                  className={TWclassNames(
                    "h-4 w-4 transition-transform duration-200",
                    subIsOpen ? "rotate-90" : ""
                  )}
                />
              </button>
              {subHasChildren && subIsOpen && (
                <ul className="pt-1 pl-6 flex flex-col gap-1">
                  {subSidebarChildren.map((nestedItem) => (
                    <RoleOnly
                      key={`nested-${key}-${nestedItem.title}`}
                      role={nestedItem?.role}
                      roles={nestedItem?.roles}
                      feature={nestedItem?.feature}
                      action={nestedItem?.action}
                      requireAdmin={nestedItem?.requireAdmin}
                      requireAllRoles={nestedItem?.requireAllRoles}
                      drivingFleetVehiclesList={
                        nestedItem?.drivingFleetVehiclesList
                      }
                    >
                      <li className="min-w-0">
                        <Link
                          to={nestedItem.path}
                          onClick={() => onClick()}
                          className={TWclassNames(
                            isActiveRoute(nestedItem.path) ? "pd-sidebar-nav__item--active" : "",
                            "pd-sidebar-nav__item group flex items-center whitespace-nowrap rounded-md py-1.5 pl-2 pr-2 text-xs transition-colors"
                          )}
                          aria-current={isActiveRoute(nestedItem.path) ? "page" : undefined}
                        >
                          <ArrowRightIcon className="h-3 w-3 mr-2" />
                          {menuLabel(nestedItem.title)}
                        </Link>
                      </li>
                    </RoleOnly>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      );
    }
  };

  const renderBottomLink = (item) => {
    const current = isActiveRoute(item.path);

    return (
      <RoleOnly
        key={item.path}
        role={item?.role}
        roles={item?.roles}
        feature={item?.feature}
        action={item?.action}
        requireAdmin={item?.requireAdmin}
        requireAllRoles={item?.requireAllRoles}
      >
        <Link
          to={item.sidebarTo || item.path}
          onClick={() => onClick()}
          title={menuLabel(item.title)}
          className={TWclassNames(
            current ? "pd-sidebar-nav__item--active" : "",
            "pd-sidebar-nav__item group flex items-center gap-2 rounded-xl p-2 text-sm font-semibold cursor-pointer transition-all duration-300"
          )}
          aria-current={current ? "page" : undefined}
        >
          <item.icon className="h-6 w-6 shrink-0" />
          {!collapsed && (
            <span className="min-w-0 overflow-hidden whitespace-nowrap transition-all duration-300 w-auto opacity-100">
              {menuLabel(item.title)}
            </span>
          )}
        </Link>
      </RoleOnly>
    );
  };

  return (
    <div className="pd-theme pd-sidebar-nav flex min-h-0 flex-1 flex-col" data-theme={settings?.theme === "dark" ? "dark" : "light"}>
      <ul
        role="list"
        className={TWclassNames(
          "pm-sidebar-nav-scroll flex min-h-0 flex-1 flex-col gap-y-0 overflow-y-auto overflow-x-hidden",
          collapsed && "pm-sidebar-nav-scroll--collapsed"
        )}
      >
        <li>
          <ul role="list" className="flex flex-col gap-0 list-none [&>li]:m-0 [&>li]:p-0">
            {routes.map((item, idx) => renderMenuItem(item, 0, `route-${idx}`))}
          </ul>
        </li>
      </ul>
      {bottomRoutes.length > 0 && (
        <div className="pd-sidebar-nav__bottom mt-auto shrink-0 border-t border-gray-200 pt-2 pb-1">
          {bottomRoutes.map((item) => renderBottomLink(item))}
        </div>
      )}
    </div>
  );
});

export default SidebarNav;

SidebarNav.displayName = "SidebarNav";
SidebarNav.whyDidYouRender = true;
